import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { HeartPulse, Plus, X, CheckCircle2 } from 'lucide-react';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { entryDay } from './healthFormat';
import ModalShell, { MODAL_CANCEL_CLASS, MODAL_SUBMIT_CLASS } from '../common/ModalShell';
import {
    addHealthEntry,
    clearDuplicate,
    clearAddError,
    HEALTH_CATEGORIES,
    HEALTH_SUBTYPES,
    HEALTH_DETAIL_FIELDS,
    ONGOING_CATEGORIES,
} from '../../redux/healthRecordSlice';

// "PATIENT HEALTH RECORD" ON THE CONSULTATION SCREEN (doc/one-patient-row.md
// §8 / §8a.3, Phase 3).
//
// A doctor writes on somebody's permanent, cross-hospital record ONLY from
// inside their own appointment with them — the server refuses a doctor's POST
// that carries no `appointmentId` (400 APPOINTMENT_REQUIRED), and refuses one
// that is not both theirs and this patient's (403 NOT_YOUR_APPOINTMENT). The
// appointment IS the permission, so the form exists only where there is one to
// name: inside the "Patient health record" drawer opened from a visit (round
// 9 — the visit screen itself shows only a compact row, HealthRecordVisitRow).
//
// What is written here is Verified by construction (§8a, and the server sets
// it): the doctor saw the patient, and there is nobody left to confirm it.
//
// THE DUPLICATE DIALOG is the other half of the flow. Two people describe the
// same event — the patient typed "blood donation, 4 March" and the doctor
// records the same donation a month later — and a second row would make the
// timeline lie about how often it happened. The server answers 409
// POSSIBLE_DUPLICATE with the row it matched, and the doctor, who is the only
// one who can tell, answers: "Same" re-sends with `confirmSameOf` (which
// VERIFIES the existing row and writes nothing new), "Different" re-sends with
// `force`.

const inputClass = 'w-full rounded-xl border border-gray-300 p-2 text-sm outline-none focus:border-[#4ABDBD]';
const labelClass = 'block text-xs font-semibold text-gray-600';

/** The status a new entry carries, given its category and the ongoing toggle. */
const statusFor = (category, ongoing) => {
    if (!ONGOING_CATEGORIES.includes(category)) return null;   // server default: 'active'
    if (category === 'medicine') return ongoing ? 'current' : 'stopped';
    return ongoing ? 'active' : 'resolved';
};

function AddHealthEntrySection({ patientId, appointmentId }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { saving, addError, duplicate, lastAdded } = useSelector((state) => state.healthRecord);

    const [open, setOpen] = useState(false);
    const [category, setCategory] = useState(HEALTH_CATEGORIES[0]);
    const [subtype, setSubtype] = useState(HEALTH_SUBTYPES[HEALTH_CATEGORIES[0]][0]);
    const [title, setTitle] = useState('');
    const [occurredAt, setOccurredAt] = useState('');
    const [endedAt, setEndedAt] = useState('');
    const [ongoing, setOngoing] = useState(true);
    const [details, setDetails] = useState({});
    const [titleTouched, setTitleTouched] = useState(false);

    const subtypeOptions = HEALTH_SUBTYPES[category] || null;
    const detailFields = HEALTH_DETAIL_FIELDS[category] || [];
    const canEnd = ONGOING_CATEGORIES.includes(category);

    const body = useMemo(() => {
        const cleanDetails = {};
        Object.entries(details).forEach(([k, v]) => {
            if (v !== undefined && v !== null && String(v).trim() !== '') cleanDetails[k] = String(v).trim();
        });
        const status = statusFor(category, ongoing);
        return {
            patientId,
            category,
            // A closed-list category MUST carry one of its values; the others
            // carry none at all rather than an invented label.
            subtype: subtypeOptions ? subtype : null,
            title: title.trim(),
            details: cleanDetails,
            occurredAt: occurredAt || null,
            endedAt: canEnd && !ongoing ? (endedAt || null) : null,
            ...(status ? { status } : {}),
            // The appointment this was written inside — the doctor's whole
            // right to write here.
            appointmentId,
        };
    }, [patientId, category, subtypeOptions, subtype, title, details, occurredAt, endedAt, ongoing, canEnd, appointmentId]);

    const reset = () => {
        setTitle('');
        setOccurredAt('');
        setEndedAt('');
        setOngoing(true);
        setDetails({});
        setTitleTouched(false);
    };

    const pickCategory = (next) => {
        setCategory(next);
        setSubtype(HEALTH_SUBTYPES[next] ? HEALTH_SUBTYPES[next][0] : '');
        // Details belong to the category that asked for them; carrying a
        // surgeon's name over to a vaccination would silently store nonsense.
        setDetails({});
        setOngoing(true);
        setEndedAt('');
    };

    const submit = async (extra = {}) => {
        setTitleTouched(true);
        if (!title.trim()) return;
        const result = await dispatch(addHealthEntry({ ...body, ...extra }));
        // A 409 leaves the form standing with what was typed, so the duplicate
        // dialog's "Different" can re-send the very same entry.
        if (addHealthEntry.fulfilled.match(result)) {
            setOpen(false);
            reset();
        }
    };

    const close = () => {
        setOpen(false);
        reset();
        dispatch(clearAddError());
    };

    const titleMissing = titleTouched && !title.trim();

    return (
        <div data-testid="health-add-section" className="mb-4 flex w-full flex-col gap-2 sm:mb-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-base font-semibold text-[#4ABDBD] sm:text-lg">
                    <HeartPulse className="h-5 w-5" aria-hidden="true" />
                    {t('healthRecord.add.section_title')}
                </h2>
                {!open && (
                    <button
                        type="button"
                        data-testid="health-add-open"
                        disabled={!appointmentId}
                        onClick={() => { dispatch(clearAddError()); setOpen(true); }}
                        className="inline-flex items-center gap-1 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-4 py-2 text-sm font-semibold text-white shadow-lg transition-all hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                        {t('healthRecord.add.button')}
                    </button>
                )}
            </div>

            <p className="text-xs text-gray-500">
                {appointmentId ? t('healthRecord.add.section_hint') : t('healthRecord.add.needs_appointment')}
            </p>

            {!open && lastAdded && (
                <p data-testid="health-add-success" className="inline-flex items-center gap-1.5 rounded-xl border border-[#4ABDBD]/30 bg-[#4ABDBD]/10 p-2 text-xs font-medium text-[#2D8A8A]">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('healthRecord.add.added')} <span className="font-semibold">{lastAdded.title}</span>
                </p>
            )}

            {open && (
                <div data-testid="health-add-form" className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                            <label className={labelClass} htmlFor="health-add-category">{t('healthRecord.add.category')}</label>
                            <select
                                id="health-add-category"
                                data-testid="health-add-category"
                                value={category}
                                onChange={(e) => pickCategory(e.target.value)}
                                className={inputClass}
                            >
                                {HEALTH_CATEGORIES.map((c) => (
                                    <option key={c} value={c}>{t(`healthRecord.categories.${c}`)}</option>
                                ))}
                            </select>
                        </div>

                        {subtypeOptions && (
                            <div>
                                <label className={labelClass} htmlFor="health-add-subtype">{t('healthRecord.add.subtype')}</label>
                                <select
                                    id="health-add-subtype"
                                    data-testid="health-add-subtype"
                                    value={subtype}
                                    onChange={(e) => setSubtype(e.target.value)}
                                    className={inputClass}
                                >
                                    {subtypeOptions.map((s) => (
                                        <option key={s} value={s}>{t(`healthRecord.subtypes.${s}`)}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className={subtypeOptions ? 'sm:col-span-2' : ''}>
                            <label className={labelClass} htmlFor="health-add-title">{t('healthRecord.add.title')}</label>
                            <input
                                id="health-add-title"
                                data-testid="health-add-title"
                                type="text"
                                value={title}
                                maxLength={255}
                                onChange={(e) => setTitle(e.target.value)}
                                onBlur={() => setTitleTouched(true)}
                                placeholder={t('healthRecord.add.title_placeholder')}
                                className={inputClass}
                            />
                            {titleMissing && (
                                <p data-testid="health-add-title-error" className="mt-1 text-xs text-red-600">{t('healthRecord.add.title_required')}</p>
                            )}
                        </div>

                        <div>
                            <label className={labelClass} htmlFor="health-add-date">{t('healthRecord.add.date')}</label>
                            <input
                                id="health-add-date"
                                data-testid="health-add-date"
                                type="date"
                                value={occurredAt}
                                onChange={(e) => setOccurredAt(e.target.value)}
                                className={inputClass}
                            />
                        </div>

                        {canEnd && (
                            <div className="flex flex-col justify-end gap-1">
                                <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">
                                    <input
                                        data-testid="health-add-ongoing"
                                        type="checkbox"
                                        checked={ongoing}
                                        onChange={(e) => setOngoing(e.target.checked)}
                                        className="h-4 w-4 accent-[#4ABDBD]"
                                    />
                                    {t('healthRecord.add.ongoing')}
                                </label>
                                {!ongoing && (
                                    <input
                                        data-testid="health-add-ended"
                                        type="date"
                                        aria-label={t('healthRecord.add.ended')}
                                        value={endedAt}
                                        onChange={(e) => setEndedAt(e.target.value)}
                                        className={inputClass}
                                    />
                                )}
                            </div>
                        )}

                        {detailFields.map((field) => (
                            <div key={field.name}>
                                <label className={labelClass} htmlFor={`health-add-detail-${field.name}`}>
                                    {t(`healthRecord.details.${field.name}`)}
                                </label>
                                {field.type === 'select' ? (
                                    <select
                                        id={`health-add-detail-${field.name}`}
                                        data-testid={`health-add-detail-${field.name}`}
                                        value={details[field.name] || ''}
                                        onChange={(e) => setDetails((d) => ({ ...d, [field.name]: e.target.value }))}
                                        className={inputClass}
                                    >
                                        <option value="">{t('healthRecord.add.subtype_none')}</option>
                                        {field.options.map((o) => (
                                            <option key={o} value={o}>{t(`healthRecord.severity.${o}`, o)}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        id={`health-add-detail-${field.name}`}
                                        data-testid={`health-add-detail-${field.name}`}
                                        type="text"
                                        value={details[field.name] || ''}
                                        onChange={(e) => setDetails((d) => ({ ...d, [field.name]: e.target.value }))}
                                        className={inputClass}
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    {addError && (
                        <p data-testid="health-add-error" className="mt-3 rounded-xl border border-red-100 bg-red-50 p-2 text-xs text-red-700">
                            {extractErrorMessage(addError, t('healthRecord.add.failed'))}
                        </p>
                    )}

                    <div className="mt-4 flex justify-end gap-2">
                        <button
                            type="button"
                            data-testid="health-add-cancel"
                            onClick={close}
                            className="inline-flex items-center gap-1 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50"
                        >
                            <X className="h-4 w-4" aria-hidden="true" />
                            {t('healthRecord.add.cancel')}
                        </button>
                        <button
                            type="button"
                            data-testid="health-add-submit"
                            disabled={saving}
                            onClick={() => submit()}
                            className="rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-4 py-2 text-sm font-semibold text-white shadow-lg transition-all hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {saving ? t('healthRecord.add.saving') : t('healthRecord.add.submit')}
                        </button>
                    </div>
                </div>
            )}

            {duplicate && (
                <ModalShell
                    title={t('healthRecord.duplicate.title')}
                    size="md"
                    testId="health-duplicate-dialog"
                    footer={(
                        <>
                            <button
                                type="button"
                                data-testid="health-duplicate-different"
                                disabled={saving}
                                onClick={() => { dispatch(clearDuplicate()); submit({ force: true }); }}
                                className={MODAL_CANCEL_CLASS}
                            >
                                {t('healthRecord.duplicate.different')}
                            </button>
                            <button
                                type="button"
                                data-testid="health-duplicate-same"
                                disabled={saving}
                                onClick={() => { dispatch(clearDuplicate()); submit({ confirmSameOf: duplicate.entryId }); }}
                                className={MODAL_SUBMIT_CLASS}
                            >
                                {t('healthRecord.duplicate.same')}
                            </button>
                        </>
                    )}
                >
                        <p data-testid="health-duplicate-body" className="text-sm text-gray-600">
                            {t('healthRecord.duplicate.body', {
                                title: duplicate.title,
                                date: entryDay(duplicate) || t('healthRecord.duplicate.no_date'),
                                who: duplicate.recordedByName,
                            })}
                        </p>
                </ModalShell>
            )}
        </div>
    );
}

export default AddHealthEntrySection;
